import { useState } from 'react';
import { ACCENT, CONTEXT, CONTEXT_DIM, GRID, INK, INK_MUTED } from './palette';

/**
 * Completion rate across the inference concurrency sweep, relative to the assembled stack at each point (1.0×).
 * Accent for the Fnk stack, context greys for the two it is measured against.
 * `bestOnly` (the benchmark pages) drops the control arm and labels the axis as percentage leads instead of multiples.
 */

const W = 560;
const H = 250;
const PAD_L = 52;
const PAD_R = 20;
const PAD_T = 20;
const PAD_B = 46;
const Y_MIN = 0.75;
const Y_MAX = 2.25;
const LEVELS = ['4', '8', '16', '32', '64'];

interface Series {
  label: string;
  sub: string;
  values: number[];
  color: string;
  dashed?: boolean;
  accent?: boolean;
}

const series: Series[] = [
  { label: 'Assembled stack', sub: 'Spring MVC + LangChain4j + vLLM', values: [1, 1, 1, 1, 1], color: CONTEXT },
  { label: 'Reactive backend over vLLM', sub: 'the control arm — reactive application, same HTTP boundary to the engine', values: [1, 0.99, 0.99, 0.96, 1.12], color: CONTEXT_DIM, dashed: true },
  { label: 'Fnk AI stack', sub: 'the capacity contract continued through the boundary to the engine', values: [1.46, 1.36, 1.53, 1.68, 2.04], color: ACCENT, accent: true },
];

const x = (i: number) => PAD_L + (i / (LEVELS.length - 1)) * (W - PAD_L - PAD_R);
const y = (v: number) => H - PAD_B - ((v - Y_MIN) / (Y_MAX - Y_MIN)) * (H - PAD_T - PAD_B);
const path = (values: number[]) => values.map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(v)}`).join(' ');

interface ConcurrencySweepChartProps {
  bestOnly?: boolean;
}

const lead = (v: number) => (v === 1 ? 'baseline' : `+${Math.round((v - 1) * 100)}%`);

export function ConcurrencySweepChart({ bestOnly = false }: ConcurrencySweepChartProps) {
  const [hovered, setHovered] = useState<string | null>(null);
  const shown = bestOnly ? series.filter((s) => !s.dashed) : series;

  return (
    <figure className="m-0">
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="h-auto w-full"
        role="img"
        aria-label={
          bestOnly
            ? 'Completion-rate lead of the Fnk stack over the assembled stack across inference concurrency 4 to 64: better at every point, up to 104% better at 64.'
            : 'Completion rate relative to the assembled stack across inference concurrency 4 to 64. The reactive control stays near 1.0× and reaches 1.1× only at 64; the Fnk stack leads at every point, from 1.5× to 2.0×.'
        }
      >
        {[1, 1.5, 2].map((g) => (
          <g key={g}>
            <line x1={PAD_L} x2={W - PAD_R} y1={y(g)} y2={y(g)} stroke={GRID} strokeWidth="1" strokeDasharray={g === 1 ? undefined : '2 4'} />
            <text x={PAD_L - 10} y={y(g) + 4} textAnchor="end" fontSize="10" fill={INK_MUTED} fontFamily="IBM Plex Mono, monospace">
              {bestOnly ? lead(g) : `${g.toFixed(1)}×`}
            </text>
          </g>
        ))}
        {LEVELS.map((label, i) => (
          <text key={label} x={x(i)} y={H - PAD_B + 18} textAnchor="middle" fontSize="10" fill={INK_MUTED} fontFamily="IBM Plex Mono, monospace">
            {label}
          </text>
        ))}
        <text x={(PAD_L + W - PAD_R) / 2} y={H - 8} textAnchor="middle" fontSize="10" fill={INK_MUTED}>
          Inference concurrency limit
        </text>
        {shown.map((s) => {
          const dim = hovered !== null && hovered !== s.label;
          return (
            <g key={s.label} opacity={dim ? 0.3 : 1}>
              <path d={path(s.values)} fill="none" stroke={s.color} strokeWidth={s.accent ? 2.5 : 1.5} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={s.dashed ? '4 4' : undefined} />
              {s.values.map((v, i) => (
                <circle key={i} cx={x(i)} cy={y(v)} r={s.accent ? 3.5 : 2.5} fill={s.color} />
              ))}
            </g>
          );
        })}
        <text x={x(4) - 8} y={y(2.04) - 10} textAnchor="end" fontSize="11" fontWeight="600" fill={ACCENT} fontFamily="IBM Plex Mono, monospace">
          {bestOnly ? 'up to 104% better' : '2.0×'}
        </text>
        <text x={x(4) - 8} y={y(1) + 16} textAnchor="end" fontSize="11" fill={INK} fontFamily="IBM Plex Mono, monospace">
          {bestOnly ? 'baseline' : '1.0×'}
        </text>
      </svg>

      <div className="mt-4 space-y-2">
        {shown.map((s) => (
          <div
            key={s.label}
            className="flex cursor-default items-baseline gap-2.5"
            onMouseEnter={() => setHovered(s.label)}
            onMouseLeave={() => setHovered(null)}
            onFocus={() => setHovered(s.label)}
            onBlur={() => setHovered(null)}
            tabIndex={0}
          >
            <span className="mt-1.5 h-0.5 w-4 shrink-0" style={{ background: s.color, opacity: hovered && hovered !== s.label ? 0.4 : 1 }} aria-hidden="true" />
            <p className="text-[11px] leading-relaxed">
              <span className={s.accent ? 'font-semibold text-ink' : 'text-ink-2'}>{s.label}</span>{' '}
              <span className="text-ink-4">— {s.sub}</span>
            </p>
          </div>
        ))}
      </div>

      <div className="mt-4 border-t border-line pt-4">
        <p className="text-[13px] leading-relaxed text-ink-2">
          <span className="font-semibold text-ink">The lead holds at every point and widens with load.</span> Between concurrency 32 and 64 the
          assembled stack barely moved — it had effectively stopped scaling — while the Fnk stack kept climbing, to {bestOnly ? 'up to 104% better' : '2.0×'} at 64. The curves are not converging.
        </p>
        <p className="mt-2 text-[11px] leading-relaxed text-ink-3">
          Paired run: same model, same workload, same hardware; three machines across a 2.5GbE LAN, 50 concurrent chat users.
          Each point is the completion rate relative to the assembled stack at the same concurrency. One run per stack per point — five points in the same direction, not a confidence interval.
        </p>
      </div>
    </figure>
  );
}
